// A short two-tone notification chime synthesized with WebAudio — no audio
// asset to load (the widget stays a single self-contained bundle), no
// network fetch, ~0.28s total.
//
// Browser autoplay policy: an AudioContext created before any user gesture
// starts 'suspended' and produces silence. The one-time unlock listeners
// below resume it on the first gesture anywhere on the page, after which
// notifications are audible even when they arrive with the widget closed
// and untouched. If no gesture has happened yet, playNotificationSound()
// simply does nothing audible — never an error.

let context: AudioContext | null = null;
let unlockInstalled = false;

function getContext(): AudioContext | null {
  if (context) {
    return context;
  }

  try {
    context = new AudioContext();
  } catch {
    return null;
  }

  return context;
}

function installUnlockListeners(): void {
  if (unlockInstalled) {
    return;
  }

  unlockInstalled = true;

  const unlock = (): void => {
    const ctx = getContext();

    if (ctx && ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }

    document.removeEventListener('pointerdown', unlock);
    document.removeEventListener('keydown', unlock);
  };

  document.addEventListener('pointerdown', unlock);
  document.addEventListener('keydown', unlock);
}

function tone(ctx: AudioContext, frequency: number, startAt: number, duration: number): void {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;

  // Quick fade-in/out envelope so the tone never clicks or pops.
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(0.08, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration);
}

export function prepareNotificationSound(): void {
  installUnlockListeners();
}

export function playNotificationSound(): void {
  try {
    const ctx = getContext();

    if (!ctx) {
      return;
    }

    if (ctx.state === 'suspended') {
      // A gesture may have happened since creation — try once, and play
      // regardless: if still suspended, the tones are simply silent.
      void ctx.resume().catch(() => undefined);
    }

    const now = ctx.currentTime;
    tone(ctx, 740, now, 0.16);
    tone(ctx, 988, now + 0.12, 0.16);
  } catch {
    // Sound is decoration — no failure here may ever surface.
  }
}
